// apps/admin/src/components/shell/AdminSidebar.tsx
//
// Refactored admin sidebar (replaces the ~840-line inline AdminSidebar that
// previously lived in App.tsx). Built via /vg:debug 2026-05-20 to address
// five reported defects:
//
//   1. Sidebar links caused full-page reloads (raw <a href> instead of SPA
//      Link/NavLink) — replaced with react-router-dom <NavLink>.
//   2. Sub-items were rendered only when their parent path was active, with
//      no independent expand/collapse — replaced with useState<Set<string>>
//      driven expand state persisted to localStorage and animated chevron.
//   3. Menu structure was repeated inline JSX (~1000 lines, 20+ Billing
//      duplicates) — replaced with a declarative `SECTIONS` config + single
//      recursive renderer. New leaves require ONE entry, not 25 lines of JSX.
//   4. (n/a — user numbering skipped 4.)
//   5. Icons were duplicated across many leaves (Icon.vendors used for both
//      Vendors and Stores, Icon.dashboard reused for 11 Home-Management
//      entries, etc.) — replaced with a per-leaf unique lucide-react icon.
//   6. Lock state was rendered as the literal "🔒" emoji inside labels —
//      replaced with the lucide-react <Lock/> icon in a `trailing` slot.
//
// Permission gating: canSee() checks the backend permissions with the shared
// matcher in lib/permission-match.ts (SEC-5 — no role-name grants).
//
// Phase 4.7 Task 19 — added the `Shipping` Operations leaf
// (gated MANAGE_SHIPPING → /shipping 7-tab shell).
//
// Phase 4.7 Amendment #3 Wave 2 Task 18 — the single `Shipping`
// leaf is REPLACED by a `Shipping` menu group (base MANAGE_SHIPPING) that
// expands to FOUR child links: Shipping Price / Track Routing / Config /
// Rate Table. The Rate Table link is HIDDEN without VIEW_FINANCIAL_COST
// . Group + child descriptors are imported from the
// shipping feature dir (ShippingMenuGroup.tsx) so the declaration lives with
// the feature; the recursive renderer + canSee() permission pruning are
// reused unchanged (Rate Table child carries `permission: VIEW_FINANCIAL_COST`
// so the existing child-prune step hides it).
// vg-binding: CONTEXT:decisions=P4.7.D-21,P4.7.D-51,P4.7.D-23,P4.7.D-24,P4.7.D-17
// vg-binding: INTERFACE-STANDARDS:error-shape

import {
  type ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  Building2,
  Store,
  Users,
  FileText,
  FileSpreadsheet,
  CreditCard,
  Receipt,
  Banknote,
  ArrowDownToLine,
  Wallet,
  Coins,
  Landmark,
  ShieldAlert,
  Mail,
  RefreshCw,
  ScrollText,
  SlidersHorizontal,
  ClipboardList,
  ListChecks,
  CheckCheck,
  Send,
  PiggyBank,
  AlertTriangle,
  Globe,
  Settings,
  Cog,
  ShieldCheck,
  KeyRound,
  Megaphone,
  Vote,
  UserCog,
  Flag,
  Activity,
  Wrench,
  Bug,
  MailOpen,
  Lock,
  ChevronRight,
  LifeBuoy,
  TicketCheck,
  BookOpen,
  Newspaper,
  HelpCircle,
  Sparkles,
  Layout as LayoutIcon,
  ClipboardCheck,
  Package,
  Layers,
  Palette,
  Tags,
  Boxes,
  PlusSquare,
  Hash,
  Brush,
  FileBox,
  PackageOpen,
  BarChart3,
  Truck,
  AlertOctagon,
  History,
  Download,
  ServerCog,
  Webhook,
  Inbox,
  Frame,
  Gauge,
  Settings2,
  Split,
  // Phase 17 Task 30 — Vendor Routing leaf. Aliased because `Route` collides
  // with the react-router-dom export of the same name at a glance.
  Route as RouteIcon,
  type LucideProps,
} from 'lucide-react';

import { cn, Sidebar, sidebarNavItemActiveClassName, useAuth } from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import { PERMISSIONS, type Permission } from '../../lib/permissions.js';
import { hasGrant } from '../../lib/permission-match.js';
import {
  useNotificationStore,
  type SidebarBadgeKey,
} from '../../stores/useNotificationStore.js';
import {
  SHIPPING_MENU_GROUP,
  SHIPPING_CHILD_LINKS,
} from '../../features/shipping/components/ShippingMenuGroup.js';

// ──────────────────────────────────────────────────────────────────────
// Declarative menu config — every leaf has a UNIQUE lucide icon.
// ──────────────────────────────────────────────────────────────────────

type IconCmp = ComponentType<LucideProps>;

interface LeafItem {
  kind: 'leaf';
  /** Stable key used for data-testid + React key. */
  key: string;
  /** Translation key OR raw label. If `labelKey` set, t(labelKey) wins. */
  labelKey?: string;
  label?: string;
  to: string;
  /** Extra paths that should also activate this leaf (e.g. legacy routes). */
  matchPaths?: string[];
  /**
   * Exact-match only. For "hub" leaves whose `to` is a prefix of sibling
   * routes (e.g. `/affiliate` is a prefix of `/affiliate/list`), prefix
   * matching lights up BOTH the hub and the child. Set exact:true so the
   * hub activates only on its own pathname. Debug dbg-20260529-2009.
   */
  exact?: boolean;
  /**
   * Prefixes that must NOT activate this leaf even though they start with
   * `to`. For a hub leaf (`/vendors`) that should stay lit on its own detail
   * routes (`/vendors/:id`) but NOT on a sibling leaf with its own nav entry
   * (`/vendors/allocations`), list the sibling here. dbg-20260720.
   */
  excludePaths?: string[];
  icon: IconCmp;
  /** Permission gate. Omit = always visible. */
  permission?: Permission | string;
  /** SA-only — renders trailing <Lock/> icon. */
  saOnly?: boolean;
  /** Trailing unread-count pill fed from useNotificationStore.pendingCountByKey. */
  badgeKey?: SidebarBadgeKey;
  /**
   * Explicit data-testid override. When omitted the renderer derives
   * `sidebar-${key}-link`. Set when a stable codegen selector is required
   * verbatim (e.g. Phase 4.7 PLAN <test_ids> shipping-nav-*).
   */
  testId?: string;
}

interface GroupItem {
  kind: 'group';
  key: string;
  labelKey?: string;
  label?: string;
  icon: IconCmp;
  /**
   * Permission gate for the parent. If any child is visible without an
   * explicit parent permission, the group still shows.
   */
  permission?: Permission | string;
  /**
   * When true (default), the group renders the parent row WITHOUT a route
   * (clicking only toggles expand). Set `to` to make the parent itself a
   * route AND a toggle target.
   */
  to?: string;
  /**
   * Explicit data-testid override for the group toggle. When omitted the
   * renderer derives `sidebar-${key}-group-toggle`. Set when a stable codegen
   * selector is required verbatim (e.g. Phase 4.7 shipping-nav-group).
   */
  testId?: string;
  /** Trailing unread-count pill fed from useNotificationStore.pendingCountByKey. */
  badgeKey?: SidebarBadgeKey;
  children: LeafItem[];
}

interface Section {
  /** Section heading label key (or raw label). Optional — no heading rendered when both omitted. */
  headingKey?: string;
  heading?: string;
  /** Section is visible only when at least one item resolves visible. */
  items: Array<LeafItem | GroupItem>;
}

// Per-child icon map for the Shipping menu group (Task 18) — keyed by the
// child link `key` so the descriptor list in ShippingMenuGroup.tsx stays
// icon-agnostic (icons are a shell concern, sidebar convention §5).
const SHIPPING_CHILD_ICONS: Record<string, IconCmp> = {
  'shipping.price': Coins,
  'shipping.trackRouting': Activity,
  'shipping.config': SlidersHorizontal,
  'shipping.rt2Rate': FileSpreadsheet,
};

const SECTIONS: Section[] = [
  // ── Operations (no heading — these are the most-used top-level entries) ──
  {
    items: [
      {
        kind: 'leaf',
        key: 'dashboard',
        labelKey: 'dashboard',
        to: '/',
        matchPaths: ['/dashboard'],
        icon: LayoutDashboard,
      },
      // Orders is a direct link to the order list (no dropdown). Order
      // Settings is reachable via the "Go to settings" button on the Orders
      // page (→ /orders/settings). vg-binding: CONTEXT:P8.D-555
      {
        kind: 'leaf',
        key: 'orders',
        labelKey: 'orders',
        to: '/orders',
        icon: ShoppingCart,
      },
      {
        kind: 'leaf',
        key: 'merchants',
        labelKey: 'merchants',
        to: '/merchants',
        icon: Building2,
      },
      // Vendors — a GROUP with two children (dbg-20260811-150000,
      // figma-v3-vendor-admin.pen draws the submenu): Vendor and Service.
      //
      // It was a plain leaf before. It had been a group once and was flattened
      // because its ONLY child was gated on PRODUCTS_BLUEPRINT_MANAGE, and the
      // group-prune (children.length === 0 → null) then hid the entire Vendors
      // menu from anyone without that grant. Both children here are gated the
      // same way the group is, so that trap cannot come back: whoever sees the
      // group sees at least one child.
      //
      // `to` is set, so the parent row is both a link to /vendors and the
      // expand toggle — the design shows Vendor selected under an open Vendors
      // hub, which needs the parent to be navigable.
      //
      // `exact` on the Vendor child: `/vendors` is a prefix of every sibling
      // route (/vendors/services, /vendors/allocations, /vendors/routing), and
      // without it the child stays lit on all of them. This is the fourth time
      // that prefix collision has been fixed in this file — a child whose `to`
      // is a prefix of a sibling's ALWAYS needs `exact`.
      {
        kind: 'group',
        key: 'vendors',
        labelKey: 'vendors',
        to: '/vendors',
        icon: Users,
        children: [
          {
            kind: 'leaf',
            key: 'vendorsList',
            labelKey: 'vendors',
            to: '/vendors',
            exact: true,
            // The list's own detail/edit/create routes still light this child;
            // they are not separate nav entries.
            matchPaths: ['/vendors/manage', '/vendors/create', '/vendors/pending'],
            icon: Users,
          },
          {
            kind: 'leaf',
            key: 'vendorsServices',
            labelKey: 'vendorsServices',
            to: '/vendors/services',
            icon: Truck,
          },
        ],
      },
      // Phase 4.4.2 Wave 5 Task 18 — Vendor Allocations (config split + per-vendor
      // stats + change-proposal approvals). Leaf sits directly under Vendors;
      // gated on vendor_allocations:view via canSee() child-prune (BE authoritative).
      // Label resolves cross-namespace (vendorAllocations:nav.allocations) so no
      // new navigation-ns key is added.
      // vg-binding: CONTEXT:decisions=P4.4.2.D-14,P4.4.2.D-16
      // vg-binding: INTERFACE-STANDARDS:error-shape
      {
        kind: 'leaf',
        key: 'vendorAllocations',
        labelKey: 'vendorAllocations:nav.allocations',
        to: '/vendors/allocations',
        icon: Split,
        permission: 'vendor_allocations:view',
      },
      // Phase 17 Wave 8 Task 30 — Vendor Routing Governance (scoring weights +
      // merchant block/allow rules + vendor preferences + fence report). Leaf
      // sits beside Allocations because the two are siblings in the UI but
      // different concepts: allocation % splits volume among eligible vendors,
      // routing governance decides eligibility (P17.D-20).
      // Hidden by the canSee() child-prune without
      // orders:manage_assignment_rules — cosmetic only; the BE gate (task-24)
      // is authoritative and a typed URL still renders the admin 403 page
      // (P17.D-21). Label resolves cross-namespace (vendorRouting:nav.routing)
      // so no new navigation-ns key is added. testId is PLAN task-30
      // <test_ids> verbatim — the derived default would be
      // `sidebar-vendorRouting-link`.
      // vg-binding: CONTEXT:decisions=P17.D-20,P17.D-21,P17.D-26
      // vg-binding: INTERFACE-STANDARDS:error-shape
      {
        kind: 'leaf',
        key: 'vendorRouting',
        labelKey: 'vendorRouting:nav.routing',
        to: '/vendors/routing',
        testId: 'sidebar-vendors-routing-link',
        icon: RouteIcon,
        permission: PERMISSIONS.MANAGE_ASSIGNMENT_RULES,
      },
      {
        kind: 'leaf',
        key: 'stores',
        labelKey: 'stores',
        to: '/stores',
        icon: Store,
      },
      // Phase 4.7 Amendment #3 Wave 2 Task 18 — Shipping menu
      // group (base MANAGE_SHIPPING) with FOUR child links. Replaces the old
      // single `/shipping` leaf. The Rate Table child carries
      // `permission: VIEW_FINANCIAL_COST` so the existing canSee() child-prune
      // hides it without the cost grant; FE-hide is UX-only
      // (API authoritative, Task 17). Group toggle testId + child link testIds
      // are PLAN <test_ids> verbatim (shipping-nav-group / shipping-nav-*).
      {
        kind: 'group',
        key: SHIPPING_MENU_GROUP.key,
        labelKey: SHIPPING_MENU_GROUP.labelKey,
        testId: SHIPPING_MENU_GROUP.groupTestId,
        icon: Truck,
        permission: SHIPPING_MENU_GROUP.permission,
        children: SHIPPING_CHILD_LINKS.map(
          (child): LeafItem => ({
            kind: 'leaf',
            key: child.key,
            labelKey: child.labelKey,
            to: child.to,
            testId: child.testId,
            permission: child.permission,
            // Forward the descriptor's exact-match flag — `/shipping/rate-table-2`
            // is a prefix of its own rt2* submenus, so without this it stayed lit
            // alongside them. dbg-20260730-072612.
            ...(child.exact ? { exact: true as const } : {}),
            // Per-child unique lucide icon (sidebar convention §5): Coins for
            // Price, Activity for Track Routing, SlidersHorizontal for Config,
            // FileSpreadsheet for Rate Table.
            icon: SHIPPING_CHILD_ICONS[child.key] ?? Truck,
          }),
        ),
      },
    ],
  },

  // ── Finance / Billing (collapsible — group label IS the heading) ──
  {
    items: [
      {
        kind: 'group',
        key: 'billing',
        labelKey: 'groups.billing',
        icon: CreditCard,
        children: [
          // Per-item RBAC (wallet handover sweep b11): `permission:` is set ONLY
          // where the BE route enforces that exact key via requirePermission
          // today. Items whose BE has only adminRoleGuard stay ungated on
          // purpose — inventing an FE-only key would hide the menu from admins
          // the BE happily serves. Residual gap (invoice/credit-memo/refund/
          // aging/legal-entity/payout/transaction routes are role-gated only)
          // needs a shipping-v2-style perm matrix on the BE first.
          { kind: 'leaf', key: 'fin.invoices', labelKey: 'finance.invoices', to: '/admin/billing/invoices', matchPaths: ['/billing/invoices'], icon: FileText },
          { kind: 'leaf', key: 'fin.aging', labelKey: 'finance.agingReport', to: '/admin/billing/aging-report', matchPaths: ['/billing/aging-report'], icon: ClipboardList },
          { kind: 'leaf', key: 'fin.refundTasks', labelKey: 'finance.refundTasks', to: '/admin/billing/refund-tasks', matchPaths: ['/billing/refund-tasks'], icon: RefreshCw },
          { kind: 'leaf', key: 'fin.creditMemos', labelKey: 'finance.creditMemos', to: '/admin/billing/credit-memos', matchPaths: ['/billing/credit-memos'], icon: Receipt },
          { kind: 'leaf', key: 'fin.cmApprovals', labelKey: 'finance.pendingApprovals', to: '/admin/billing/credit-memo-approvals', icon: CheckCheck },
          { kind: 'leaf', key: 'fin.paymentRequests', labelKey: 'finance.paymentRequests', to: '/admin/billing/payment-requests', icon: Send },
          { kind: 'leaf', key: 'fin.transactions', labelKey: 'finance.transactions', to: '/admin/billing/transactions', icon: ScrollText },
          { kind: 'leaf', key: 'fin.payouts', labelKey: 'finance.payouts', to: '/admin/billing/payouts', matchPaths: ['/billing/payouts'], icon: ArrowDownToLine },
          { kind: 'leaf', key: 'fin.topupReviews', labelKey: 'finance.topupReviews', to: '/admin/billing/topup-reviews', matchPaths: ['/billing/topup-reviews'], icon: ListChecks, permission: PERMISSIONS.BILLING_TOPUP_MANUAL },
          { kind: 'leaf', key: 'fin.credits', labelKey: 'finance.credits', to: '/admin/billing/credits', matchPaths: ['/admin/credits'], icon: Coins, permission: PERMISSIONS.CREDIT_READ },
          { kind: 'leaf', key: 'fin.gateways', labelKey: 'finance.gateways', to: '/admin/billing/gateways', matchPaths: ['/billing/gateways'], icon: Wallet, permission: PERMISSIONS.BILLING_SETTINGS_MANAGE },
          { kind: 'leaf', key: 'fin.transferGroups', labelKey: 'finance.transferGroups', to: '/admin/billing/transfer-groups', matchPaths: ['/billing/transfer-groups'], icon: Banknote, permission: PERMISSIONS.BILLING_TRANSFER_GROUP_MANAGE },
          { kind: 'leaf', key: 'fin.legalEntities', labelKey: 'finance.legalEntities', to: '/admin/finance/legal-entities', matchPaths: ['/admin/legal-entities'], icon: Landmark },
          // FIFO topup work-queue. Removed 2026-07-14 as a "duplicate" of
          // "Duyệt nạp tiền" (fin.topupReviews) above; restored 2026-07-25
          // (dbg-20260725-uat-fin-2629-2632) because only this page has the
          // FIFO ordering label, Xuất CSV, the suspicious-flag toggle and the
          // Bị gắn cờ tab that UAT-FIN-014 (#2631) exercises.
          { kind: 'leaf', key: 'fin.topupQueue', labelKey: 'finance.topupQueue', to: '/admin/billing/topup-queue', matchPaths: ['/billing/topup-queue'], icon: PiggyBank, permission: PERMISSIONS.BILLING_TOPUP_MANUAL },
          { kind: 'leaf', key: 'fin.withdrawQueue', labelKey: 'finance.withdrawQueue', to: '/billing/withdraw-queue', icon: ArrowDownToLine, permission: PERMISSIONS.BILLING_WITHDRAW },
          { kind: 'leaf', key: 'fin.chargebacks', labelKey: 'finance.chargebacks', to: '/billing/chargebacks', icon: AlertTriangle, permission: PERMISSIONS.BILLING_CHARGEBACK },
          { kind: 'leaf', key: 'fin.fraud', labelKey: 'finance.fraudSettings', to: '/billing/fraud-settings', icon: ShieldAlert, permission: PERMISSIONS.BILLING_FRAUD_CONFIG },
          { kind: 'leaf', key: 'fin.whitelist', labelKey: 'finance.whitelist', to: '/whitelist', icon: ShieldCheck, permission: PERMISSIONS.BILLING_FRAUD_CONFIG },
          { kind: 'leaf', key: 'fin.imap', labelKey: 'finance.imapConfig', to: '/billing/imap-config', icon: Mail, permission: PERMISSIONS.BILLING_SETTINGS_MANAGE },
          { kind: 'leaf', key: 'fin.fxRates', labelKey: 'finance.fxRates', to: '/billing/fx-rates', icon: Globe, permission: PERMISSIONS.BILLING_SETTINGS_MANAGE },
        ],
      },
    ],
  },

  // ── Audit (top-level, was outside Billing in original) ──
  {
    items: [
      {
        kind: 'leaf',
        key: 'auditLog',
        labelKey: 'auditLog',
        to: '/admin/audit-log',
        icon: History,
      },
    ],
  },

  // ── Products (collapsible — 11 leaves, all permission-gated) ──
  {
    items: [
      {
        kind: 'group',
        key: 'products',
        labelKey: 'groups.products',
        icon: Package,
        children: [
          { kind: 'leaf', key: 'p.categories', labelKey: 'products.categories', to: '/products/categories', icon: Tags, permission: PERMISSIONS.PRODUCTS_CATEGORY_MANAGE },
          { kind: 'leaf', key: 'p.blueprints', labelKey: 'products.blueprints', to: '/products/blueprints', icon: Layers, permission: PERMISSIONS.PRODUCTS_BLUEPRINT_MANAGE },
          // dbg-wizard-iter4 (promote) — Options entity admin page.
          { kind: 'leaf', key: 'p.options', labelKey: 'products.options', to: '/products/options', icon: SlidersHorizontal, permission: PERMISSIONS.PRODUCTS_OPTION_MANAGE },
          { kind: 'leaf', key: 'p.approval', labelKey: 'products.productApproval', to: '/products/product-approval', icon: ClipboardCheck, permission: PERMISSIONS.PRODUCTS_APPROVAL_MANAGE },
          { kind: 'leaf', key: 'p.special', labelKey: 'products.productsSpecial', to: '/products/products-special', icon: Sparkles, permission: PERMISSIONS.MANAGE_PRODUCT_SPECIAL },
          { kind: 'leaf', key: 'p.bundles', labelKey: 'products.bundles', to: '/products/bundles', icon: PackageOpen, permission: PERMISSIONS.PRODUCTS_BUNDLE_MANAGE },
          { kind: 'leaf', key: 'p.mapping', labelKey: 'products.mapping', to: '/products/mapping', icon: Hash, permission: PERMISSIONS.PRODUCTS_MAPPING_MANAGE },
          // dbg-fixbug 2026-08-27 — no Collections leaf. /products/collections
          // is a redirect to /products/categories?tab=collection, so the leaf
          // was a second sidebar entry pointing at the page Categories already
          // opens — two rows, one destination. The tab inside Categories is the
          // only entry point now; the redirect route stays for old bookmarks.
          { kind: 'leaf', key: 'p.addOns', labelKey: 'products.addOns', to: '/products/add-ons', icon: PlusSquare, permission: PERMISSIONS.PRODUCTS_ADDON_MANAGE },
          // Phase 8.2.1 debug — Pricing promoted to its own top-level group
          // (peer of Products); leaf removed here. See the Pricing group below.
          { kind: 'leaf', key: 'p.techniques', labelKey: 'products.techniques', to: '/products/techniques', icon: Brush, permission: PERMISSIONS.PRODUCTS_TECHNIQUE_MANAGE },
          { kind: 'leaf', key: 'p.templates', labelKey: 'products.templates', to: '/products/templates', icon: Palette, permission: PERMISSIONS.PRODUCTS_TEMPLATE_MANAGE },
          // Phase 8.5 — Artwork Doctor catalog pages.
          { kind: 'leaf', key: 'p.materials', labelKey: 'products.materials', to: '/products/materials', icon: Layers, permission: PERMISSIONS.PRODUCTS_MATERIAL_MANAGE },
          { kind: 'leaf', key: 'p.regionErrors', labelKey: 'products.regionErrors', to: '/products/region-errors', icon: ShieldAlert, permission: PERMISSIONS.PRODUCTS_REGION_ERROR_MANAGE },
          { kind: 'leaf', key: 'p.checkTypes', labelKey: 'products.checkTypes', to: '/products/check-types', icon: ClipboardCheck, permission: PERMISSIONS.PRODUCTS_CHECK_TYPE_MANAGE },
          // Phase 8.4 Amendment #2 — Print Area catalog page.
          // vg-binding: CONTEXT:P8.4.D-51
          { kind: 'leaf', key: 'p.printAreas', labelKey: 'products.printAreas', to: '/products/print-areas', icon: Frame, permission: PERMISSIONS.PRODUCTS_PRINT_AREA_MANAGE },
          { kind: 'leaf', key: 'p.locations', labelKey: 'products.locations', to: '/products/locations', icon: Globe, permission: PERMISSIONS.PRODUCTS_BLUEPRINT_MANAGE },
          // Phase 8.2.1 Amendment #11 — Vendor Mapping
          // relocated to the Vendors nav group. Removed from Products here.
        ],
      },
    ],
  },

  // ── Pricing (top-level group — peer of Products; Phase 8.2.1 debug) ──
  // View-focused module: Blueprint Prices (read-only aggregation) + Shipping
  // Fees (canonical editor). Blueprint Cost sub-menu pending. Tiers / Deals /
  // History stay reachable at /products/pricing but are de-linked here.
  {
    items: [
      {
        kind: 'group',
        key: 'pricing',
        labelKey: 'groups.pricing',
        icon: BarChart3,
        children: [
          // vg-binding: CONTEXT:decisions (P8.D-557 — sidebar leaf renamed "Blueprint Prices" → "Prices", to /pricing/prices)
          // vg-binding: CONTEXT:decisions (P8.D-561 — Pricing "Shipping Fees" leaf removed; canonical Shipping > Shipping Price /shipping/price retained under Shipping group)
          { kind: 'leaf', key: 'pr.blueprintPrices', labelKey: 'pricing.prices', to: '/pricing/prices', icon: BarChart3, permission: PERMISSIONS.MANAGE_PRICING },
          // vg-binding: CONTEXT:decisions (P8.D-567 — additive "Prices V2" leaf → /pricing/prices-v2, V1 "Prices" leaf untouched)
          // vg-binding: CONTEXT:decisions (P8.D-572 — Prices V2 blueprint-grouped read-only view, MANAGE_PRICING gate inherited from V1)
          { kind: 'leaf', key: 'pr.blueprintPricesV2', labelKey: 'pricing.pricesV2', to: '/pricing/prices-v2', icon: BarChart3, permission: PERMISSIONS.MANAGE_PRICING },
          { kind: 'leaf', key: 'pr.blueprintCost', labelKey: 'pricing.blueprintCost', to: '/pricing/blueprint-cost', icon: Coins, permission: PERMISSIONS.MANAGE_PRICING },
          // Phase 14.1 Task 38 (F-O1) — Admin packing-group approval queue (G-32).
          { kind: 'leaf', key: 'pr.packingGroups', labelKey: 'pricing.packingGroups', to: '/pricing/packing-groups', icon: ClipboardCheck, permission: PERMISSIONS.MANAGE_PRICING },
          { kind: 'leaf', key: 'pr.pricingConfig', labelKey: 'pricing.pricingConfig', to: '/pricing/config', icon: Coins, permission: PERMISSIONS.MANAGE_PRICING },
        ],
      },
    ],
  },

  // ── Support ──
  {
    items: [
      {
        kind: 'group',
        key: 'support',
        labelKey: 'groups.support',
        icon: LifeBuoy,
        permission: PERMISSIONS.SUPPORT_TICKET_READ,
        badgeKey: 'support',
        children: [
          { kind: 'leaf', key: 'sup.dashboard', labelKey: 'support.dashboard', to: '/support/dashboard', icon: LayoutDashboard },
          { kind: 'leaf', key: 'sup.tickets', labelKey: 'support.tickets', to: '/support/tickets', icon: TicketCheck, badgeKey: 'support' },
          { kind: 'leaf', key: 'sup.departments', labelKey: 'support.departments', to: '/support/departments', icon: Building2 },
          { kind: 'leaf', key: 'sup.performance', labelKey: 'support.performance', to: '/support/performance', icon: Gauge },
          { kind: 'leaf', key: 'sup.configurations', labelKey: 'support.configurations', to: '/support/configurations', icon: Settings2 },
        ],
      },
    ],
  },

  // ── Reports (Phase 8.3) ──
  {
    items: [
      {
        kind: 'group',
        key: 'reports',
        labelKey: 'groups.reports',
        icon: BarChart3,
        permission: PERMISSIONS.VIEW_DASHBOARD,
        children: [
          { kind: 'leaf', key: 'r.analytics', labelKey: 'reports.analytics', to: '/reports/analytics', icon: BarChart3 },
          { kind: 'leaf', key: 'r.warnings', labelKey: 'reports.orderWarnings', to: '/reports/warnings', icon: AlertOctagon },
          { kind: 'leaf', key: 'r.syncErrors', labelKey: 'reports.syncErrorReports', to: '/reports/sync-errors', icon: AlertTriangle },
          { kind: 'leaf', key: 'r.auditLogs', labelKey: 'reports.auditLogs', to: '/reports/audit-logs', icon: ScrollText },
          { kind: 'leaf', key: 'r.accountantActivity', labelKey: 'reports.accountantActivity', to: '/reports/accountant-activity', icon: ScrollText },
          { kind: 'leaf', key: 'r.exportCenter', labelKey: 'reports.exportCenter', to: '/reports/export-center', icon: Download },
        ],
      },
    ],
  },

  // ── Sales / CRM (Phase 10 —/12/15/17/19/25/29/30/31/35/37/38/39/55) ──
  {
    items: [
      {
        kind: 'group',
        key: 'sales',
        labelKey: 'groups.salesCrm',
        icon: BarChart3,
        children: [
          { kind: 'leaf', key: 'sales.overview', labelKey: 'sales.overview', to: '/sales/overview', icon: LayoutDashboard },
          { kind: 'leaf', key: 'sales.leads', labelKey: 'sales.leads', to: '/sales/leads', icon: Users },
          { kind: 'leaf', key: 'sales.deals', labelKey: 'sales.deals', to: '/sales/deals', icon: FileText },
          { kind: 'leaf', key: 'sales.groups', labelKey: 'sales.salerGroups', to: '/sales/groups', icon: UserCog },
          { kind: 'leaf', key: 'sales.calendar', labelKey: 'sales.calendar', to: '/sales/calendar', icon: ClipboardList },
          { kind: 'leaf', key: 'sales.targets', labelKey: 'sales.targets', to: '/sales/targets', icon: Flag },
          { kind: 'leaf', key: 'sales.leaderboard', labelKey: 'sales.leaderboard', to: '/sales/leaderboard', icon: BarChart3 },
          { kind: 'leaf', key: 'sales.gmvReport', labelKey: 'sales.gmvReport', to: '/sales/gmv-report', icon: PiggyBank },
          // Phase 10.2 — Sales Operations analytics. Hidden without the
          // `sales_operations:read` permission (canSee() child-prune); BE also
          // enforces 403 on the read endpoints for defence in depth.
          { kind: 'leaf', key: 'sales.operations', labelKey: 'sales.operations', to: '/sales/operations', icon: Activity, permission: 'sales_operations:read' },
          { kind: 'leaf', key: 'sales.merchants', labelKey: 'sales.merchants', to: '/sales/merchants', icon: Building2 },
          { kind: 'leaf', key: 'sales.benefits', labelKey: 'sales.benefits', to: '/sales/benefits', icon: Sparkles },
          { kind: 'leaf', key: 'sales.reports', labelKey: 'sales.reports', to: '/sales/reports', icon: ScrollText },
          { kind: 'leaf', key: 'sales.settings', labelKey: 'sales.settings', to: '/admin/settings/sales', icon: Settings },
        ],
      },
    ],
  },

  // ── Affiliate (Phase 10 — ESP + commission + CPL stale) ──
  {
    items: [
      {
        kind: 'group',
        key: 'affiliate',
        labelKey: 'groups.affiliate',
        icon: Megaphone,
        children: [
          { kind: 'leaf', key: 'aff.hub', labelKey: 'affiliate.hub', to: '/affiliate', exact: true, icon: LayoutDashboard },
          { kind: 'leaf', key: 'aff.list', labelKey: 'affiliate.list', to: '/affiliate/list', icon: Users },
          { kind: 'leaf', key: 'aff.payout', labelKey: 'affiliate.payoutQueue', to: '/affiliate/payout-queue', icon: Send },
          { kind: 'leaf', key: 'aff.shortLinks', labelKey: 'affiliate.shortLinks', to: '/affiliate/short-links', icon: Hash },
          { kind: 'leaf', key: 'aff.abuse', labelKey: 'affiliate.abuseReport', to: '/affiliate/abuse-report', icon: ShieldAlert },
        ],
      },
    ],
  },

  // ── Home Management (CMS) ──
  {
    items: [
      {
        kind: 'group',
        key: 'homeMgmt',
        labelKey: 'groups.homeManagement',
        icon: LayoutIcon,
        permission: 'home_management:manage',
        children: [
          { kind: 'leaf', key: 'hm.homepage', labelKey: 'homeManagement.homepage', to: '/home-management/homepage', icon: LayoutIcon },
          { kind: 'leaf', key: 'hm.ebooks', labelKey: 'homeManagement.ebooks', to: '/home-management/ebooks', icon: BookOpen },
          { kind: 'leaf', key: 'hm.blog', labelKey: 'homeManagement.blog', to: '/home-management/blog', icon: Newspaper },
          { kind: 'leaf', key: 'hm.help', labelKey: 'homeManagement.help', to: '/home-management/help', icon: HelpCircle },
          { kind: 'leaf', key: 'hm.stories', labelKey: 'homeManagement.stories', to: '/home-management/stories', icon: Sparkles },
          { kind: 'leaf', key: 'hm.faq', labelKey: 'homeManagement.faq', to: '/home-management/faq', icon: HelpCircle },
          { kind: 'leaf', key: 'hm.sellerSurvey', labelKey: 'homeManagement.sellerSurvey', to: '/home-management/seller-survey', icon: ClipboardList },
          { kind: 'leaf', key: 'hm.footer', labelKey: 'homeManagement.footer', to: '/home-management/footer', icon: LayoutIcon },
          { kind: 'leaf', key: 'hm.contact', labelKey: 'homeManagement.contact', to: '/home-management/contact', icon: Mail },
          { kind: 'leaf', key: 'hm.pages', labelKey: 'homeManagement.pages', to: '/home-management/pages', icon: FileBox },
        ],
      },
    ],
  },

  // ── Inventory (group — G-23 sub-reports wired 2026-07-30; the five report
  //     pages existed since 8.3 but had no route/nav so the section stayed a
  //     single leaf) ──
  {
    items: [
      {
        kind: 'group',
        key: 'inv',
        labelKey: 'inventory.group',
        icon: Boxes,
        children: [
          {
            kind: 'leaf',
            key: 'inv.overview',
            labelKey: 'inventory.overview',
            to: '/inventory/overview',
            icon: Gauge,
            permission: 'inventory:read',
          },
          {
            kind: 'leaf',
            key: 'inv.stockLevel',
            labelKey: 'inventory.stockLevel',
            to: '/inventory/reports/stock-level',
            icon: Layers,
            permission: 'inventory:read',
          },
          {
            kind: 'leaf',
            key: 'inv.valuation',
            labelKey: 'inventory.valuation',
            to: '/inventory/reports/valuation',
            icon: Coins,
            permission: 'inventory:read',
          },
          {
            kind: 'leaf',
            key: 'inv.movement',
            labelKey: 'inventory.movement',
            to: '/inventory/reports/movement',
            icon: Activity,
            permission: 'inventory:read',
          },
          {
            kind: 'leaf',
            key: 'inv.aging',
            labelKey: 'inventory.aging',
            to: '/inventory/reports/aging',
            icon: History,
            permission: 'inventory:read',
          },
          {
            kind: 'leaf',
            key: 'inv.turnover',
            labelKey: 'inventory.turnover',
            to: '/inventory/reports/turnover',
            icon: RefreshCw,
            permission: 'inventory:read',
          },
        ],
      },
    ],
  },

  // ── System (collapsible — 11 leaves merged into one group, was 11 flat
  //     siblings under a redundant "System" heading) ──
  {
    items: [
      {
        kind: 'group',
        key: 'system',
        labelKey: 'groups.system',
        icon: Cog,
        children: [
          // System-wide settings (7 namespaces). Missing from this sidebar
          // entirely (dbg-20260710 #2593) so /system/settings had no clickable
          // entry — only reachable by typing the URL. Gated by the same perm
          // the page + API enforce, so it auto-hides once RBAC no longer
          // grants admin `system:settings:read`.
          {
            kind: 'leaf',
            key: 'sys.settings',
            labelKey: 'system.settings',
            to: '/system/settings/general',
            matchPaths: ['/system/settings'],
            icon: Settings,
            permission: PERMISSIONS.SYSTEM_SETTINGS_READ,
          },
          {
            kind: 'leaf',
            key: 'sys.users',
            labelKey: 'system.users',
            to: '/system/users',
            matchPaths: ['/users'],
            icon: Users,
            permission: PERMISSIONS.USER_MANAGE,
          },
          {
            kind: 'leaf',
            key: 'sys.roles',
            labelKey: 'system.roles',
            to: '/system/roles',
            matchPaths: ['/admin/roles'],
            icon: ShieldCheck,
            permission: PERMISSIONS.MANAGE_ADMIN_USERS,
          },
          {
            kind: 'leaf',
            key: 'sys.announcements',
            labelKey: 'system.announcements',
            to: '/system/announcements',
            icon: Megaphone,
            permission: 'announcement:write',
          },
          {
            kind: 'leaf',
            key: 'sys.dualApproval',
            labelKey: 'system.dualApproval',
            to: '/system/dual-approval',
            icon: Vote,
            permission: 'dual_approval:vote',
          },
          {
            kind: 'leaf',
            key: 'sys.emailTemplates',
            labelKey: 'system.emailTemplates',
            to: '/settings/email-templates',
            icon: MailOpen,
            permission: 'email_template:read',
          },
          {
            kind: 'leaf',
            key: 'sys.rolesMatrix',
            labelKey: 'system.rolesMatrix',
            to: '/settings/roles',
            icon: KeyRound,
            permission: PERMISSIONS.MANAGE_ADMIN_USERS,
          },
          {
            kind: 'leaf',
            key: 'sys.adminMgmt',
            labelKey: 'system.adminMgmt',
            to: '/system/admin-mgmt',
            icon: UserCog,
            permission: PERMISSIONS.MANAGE_ADMIN_USERS,
            saOnly: true,
          },
          {
            kind: 'leaf',
            key: 'sys.featureFlags',
            labelKey: 'system.featureFlags',
            to: '/system/feature-flags',
            icon: Flag,
            permission: PERMISSIONS.MANAGE_FEATURE_FLAGS,
            saOnly: true,
          },
          {
            kind: 'leaf',
            key: 'sys.health',
            labelKey: 'system.health',
            to: '/system/health',
            icon: Activity,
            permission: 'system:health',
            saOnly: true,
          },
          {
            kind: 'leaf',
            key: 'sys.maintenance',
            labelKey: 'system.maintenance',
            to: '/system/maintenance-mode',
            icon: Wrench,
            permission: 'system:maintenance_mode',
            saOnly: true,
          },
          {
            kind: 'leaf',
            key: 'sys.bugTracker',
            labelKey: 'system.bugTracker',
            to: '/system/bug-tracker',
            icon: Bug,
            permission: 'system:bug_tracker',
            saOnly: true,
          },
        ],
      },
    ],
  },

  // ── Monitoring ──
  {
    items: [
      {
        kind: 'group',
        key: 'monitoring',
        labelKey: 'groups.monitoring',
        icon: ServerCog,
        permission: 'monitoring:read',
        children: [
          { kind: 'leaf', key: 'mon.queue', labelKey: 'monitoring.queueJobs', to: '/monitoring/queue-jobs', icon: Activity },
          { kind: 'leaf', key: 'mon.webhooks', labelKey: 'monitoring.webhookLogs', to: '/monitoring/webhook-logs', icon: Webhook },
          { kind: 'leaf', key: 'mon.syncErrors', labelKey: 'monitoring.syncErrors', to: '/monitoring/sync-errors', icon: AlertOctagon },
          { kind: 'leaf', key: 'mon.imap', labelKey: 'monitoring.imapParser', to: '/monitoring/imap', icon: Inbox },
        ],
      },
    ],
  },

  // ── Settings (anchor at the bottom — separated by divider) ──
  {
    items: [
      {
        kind: 'leaf',
        key: 'settings',
        labelKey: 'settings',
        to: '/settings/profile',
        matchPaths: ['/settings'],
        icon: Settings,
      },
    ],
  },
];

// ──────────────────────────────────────────────────────────────────────
// Brand mark
// ──────────────────────────────────────────────────────────────────────

function PrintwayLogo(): JSX.Element {
  return (
    <div className="flex items-center">
      <img src="/printway-logo.avif" alt="Printway" className="h-8 w-auto" />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Expand-state hook — persisted to localStorage so refreshes preserve UI.
// ──────────────────────────────────────────────────────────────────────

const EXPAND_KEY = 'pw-admin-sidebar-expanded';

// Single-open accordion: restore at most ONE expanded group. The active-path
// group wins; otherwise fall back to the first persisted key. Older builds
// stored a multi-key Set, so clamp on read to avoid showing several groups
// open at once after upgrade.
function readExpanded(defaultKeys: string[]): Set<string> {
  const fallback = defaultKeys[0] ? new Set([defaultKeys[0]]) : new Set<string>();
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(EXPAND_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as string[];
    if (Array.isArray(parsed) && parsed.length > 0) {
      const sole =
        defaultKeys.find((k) => parsed.includes(k)) ?? defaultKeys[0] ?? parsed[0];
      return sole ? new Set([sole]) : new Set<string>();
    }
  } catch {
    // ignore parse errors
  }
  return fallback;
}

function useExpandedGroups(activePath: string): {
  expanded: Set<string>;
  toggle: (key: string) => void;
  isExpanded: (key: string) => boolean;
} {
  // Auto-expand groups whose child matches the active path on first mount.
  const initialKeys = useMemo(() => {
    const out: string[] = [];
    for (const section of SECTIONS) {
      for (const item of section.items) {
        if (item.kind !== 'group') continue;
        const hit = item.children.some(
          (c) =>
            activePath === c.to ||
            activePath.startsWith(`${c.to}/`) ||
            (c.matchPaths ?? []).some(
              (p) => activePath === p || activePath.startsWith(`${p}/`),
            ),
        );
        if (hit) out.push(item.key);
      }
    }
    return out;
  }, [activePath]);

  const [expanded, setExpanded] = useState<Set<string>>(() =>
    readExpanded(initialKeys),
  );

  // Persist on change.
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(
          EXPAND_KEY,
          JSON.stringify(Array.from(expanded)),
        );
      }
    } catch {
      // ignore
    }
  }, [expanded]);

  // Keep the active-path group open after navigation. Single-open: the
  // active group becomes the sole open one, collapsing any manually-opened
  // sibling. Skip when no group owns the active path (leave manual state).
  useEffect(() => {
    if (initialKeys.length === 0) return;
    setExpanded((prev) => {
      if (initialKeys.every((k) => prev.has(k)) && prev.size === initialKeys.length) {
        return prev;
      }
      return new Set(initialKeys);
    });
  }, [initialKeys]);

  // Single-open accordion: opening a group collapses every other group.
  const toggle = useCallback((key: string): void => {
    setExpanded((prev) => (prev.has(key) ? new Set() : new Set([key])));
  }, []);

  const isExpanded = useCallback(
    (key: string): boolean => expanded.has(key),
    [expanded],
  );

  return { expanded, toggle, isExpanded };
}

// ──────────────────────────────────────────────────────────────────────
// Leaf renderer — react-router NavLink (SPA navigation, no reload).
// ──────────────────────────────────────────────────────────────────────

interface LeafRowProps {
  item: LeafItem;
  /** When true, render inside a tree-connector indent. */
  nested?: boolean;
  /** Optional matcher override (used by GroupRow to highlight parent). */
  matchActive?: boolean;
  resolveLabel: (item: LeafItem) => string;
}

/**
 * Trailing unread-count pill (dbg-20260705-ticket-realtime-notify). Reads the
 * live count from useNotificationStore; renders nothing when count is 0.
 */
function NavBadge({ badgeKey }: { badgeKey: SidebarBadgeKey }): JSX.Element | null {
  const count = useNotificationStore((s) => s.pendingCountByKey[badgeKey] ?? 0);
  if (count <= 0) return null;
  return (
    <span
      data-testid={`sidebar-badge-${badgeKey}`}
      className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-pw-danger px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function LeafRow({ item, nested = false, matchActive, resolveLabel }: LeafRowProps): JSX.Element {
  const { t } = useTranslation('navigation');
  const label = resolveLabel(item);
  const testId = item.testId ?? `sidebar-${item.key.replace(/\./g, '-')}-link`;
  const IconCmp: IconCmp = item.icon;

  // matchPaths support: NavLink only matches `to` natively. We compose an
  // explicit isActive check via the `className` callback.
  const matchPaths = item.matchPaths ?? [];
  const hasMatchPath = matchPaths.length > 0;

  // Scroll the active leaf into the vertical center on mount, so a deep
  // route (e.g. /system/...) doesn't leave its nav item hidden below the
  // fold after the page loads. Debug session dbg-20260529-2009.
  const linkRef = useRef<HTMLAnchorElement | null>(null);
  useEffect(() => {
    const el = linkRef.current;
    if (!el) return;
    if (el.getAttribute('aria-current') === 'page') {
      el.scrollIntoView({ block: 'center', inline: 'nearest' });
    }
    // Run once on mount — initial route load is the only time the active
    // item may be off-screen; in-app navigation keeps it in view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NavLink
      ref={linkRef}
      to={item.to}
      end={item.to === '/' || item.exact === true}
      data-testid={testId}
      // Lay out the row.
      className={({ isActive }) => {
        const active =
          matchActive !== undefined
            ? matchActive
            : isActive ||
              (hasMatchPath &&
                matchPaths.some(
                  (p) =>
                    typeof window !== 'undefined' &&
                    (window.location.pathname === p ||
                      window.location.pathname.startsWith(`${p}/`)),
                ));
        return cn(
          // Top-level leaves match the group toggle height (h-10) so the
          // Operations items (Dashboard/Orders/Merchants/Vendors/Stores) share
          // the same vertical rhythm as the collapsible group rows below them;
          // nested children stay compact (h-9).
          'group relative flex items-center gap-3 rounded-md px-3 text-sm transition-colors',
          nested ? 'h-9 text-pw-text-muted' : 'h-10 font-medium',
          active
            ? cn(sidebarNavItemActiveClassName, 'font-medium')
            : 'text-pw-text-muted hover:bg-pw-surface hover:text-pw-text',
        );
      }}
    >
      <span
        aria-hidden="true"
        className="flex h-5 w-5 shrink-0 items-center justify-center"
      >
        <IconCmp size={nested ? 16 : 18} strokeWidth={1.75} />
      </span>
      <span className="truncate">{label}</span>
      {item.badgeKey ? <NavBadge badgeKey={item.badgeKey} /> : null}
      {item.saOnly ? (
        <span
          aria-label={t('superAdminOnly')}
          title={t('superAdminOnly')}
          className="ml-auto inline-flex items-center text-pw-text-muted"
        >
          <Lock size={12} strokeWidth={1.75} />
        </span>
      ) : null}
    </NavLink>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Group renderer — collapsible with tree connector lines + chevron.
// ──────────────────────────────────────────────────────────────────────

interface GroupRowProps {
  item: GroupItem;
  pathname: string;
  isExpanded: boolean;
  onToggle: () => void;
  resolveLabel: (item: LeafItem | GroupItem) => string;
  childIsActive: boolean;
  /**
   * Same matcher the top-level leaves use (`AdminSidebar.isLeafActive`). Passed
   * down so a group child honours `exact` / `excludePaths` / `matchPaths`
   * identically instead of falling back to NavLink's own prefix match — two
   * different rules is what let `/shipping/rate-table-2` co-highlight with its
   * rt2* submenus. dbg-20260730-072612.
   */
  matchLeaf: (leaf: LeafItem) => boolean;
}

function GroupRow({
  item,
  isExpanded,
  onToggle,
  resolveLabel,
  childIsActive,
  matchLeaf,
}: GroupRowProps): JSX.Element {
  const label = resolveLabel(item);
  const IconCmp: IconCmp = item.icon;

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={`sidebar-group-${item.key}`}
        data-testid={item.testId ?? `sidebar-${item.key}-group-toggle`}
        className={cn(
          'group flex h-10 w-full items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors',
          // Parent stays subtle even when a child is active — primary fill
          // is reserved for the leaf so users see exactly where they are.
          childIsActive
            ? 'bg-pw-surface text-pw-text'
            : 'text-pw-text-muted hover:bg-pw-surface hover:text-pw-text',
        )}
      >
        <span
          aria-hidden="true"
          className="flex h-5 w-5 shrink-0 items-center justify-center"
        >
          <IconCmp size={18} strokeWidth={1.75} />
        </span>
        <span className="flex-1 truncate text-left">{label}</span>
        {item.badgeKey ? <NavBadge badgeKey={item.badgeKey} /> : null}
        <ChevronRight
          size={16}
          strokeWidth={2}
          aria-hidden="true"
          className={cn(
            'transition-transform duration-200 ease-out',
            isExpanded ? 'rotate-90 text-pw-text' : 'text-pw-text-muted',
          )}
        />
      </button>
      {isExpanded ? (
        <div
          id={`sidebar-group-${item.key}`}
          role="group"
          aria-label={label}
          // Tree connector: vertical guide line + horizontal stub per child
          // via the `tree-child` class below.
          className="relative ml-5 mt-1 flex flex-col gap-0.5 border-l border-pw-border pl-3"
        >
          {item.children.map((child) => (
            <div key={child.key} className="tree-child relative">
              {/* Horizontal connector stub from vertical guide to row. */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -left-3 top-1/2 h-px w-3 bg-pw-border"
              />
              <LeafRow
                item={child}
                nested
                matchActive={matchLeaf(child)}
                resolveLabel={resolveLabel as (item: LeafItem) => string}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// AdminSidebar — top-level export.
// ──────────────────────────────────────────────────────────────────────

export interface AdminSidebarProps {
  activePath?: string;
}

export function AdminSidebar({ activePath = '/' }: AdminSidebarProps): JSX.Element {
  const pathname = activePath;
  const user = useAuth((s) => s.user);
  const permissions = useAuth((s) => s.permissions);
  const { t } = useTranslation('navigation');

  // SEC-5: visibility follows the backend permissions only — no grants
  // derived from the role name. Ungated items (no `permission`) always show.
  const canSee = useCallback(
    (perm?: Permission | string): boolean =>
      !perm || (user != null && hasGrant(permissions, perm)),
    [user, permissions],
  );

  const resolveLabel = useCallback(
    (item: LeafItem | GroupItem): string => {
      if (item.labelKey) return t(item.labelKey);
      return item.label ?? item.key;
    },
    [t],
  );

  const isLeafActive = useCallback(
    (leaf: LeafItem): boolean => {
      // A sibling leaf with its own nav entry (e.g. /vendors/allocations)
      // must win over the hub (/vendors) — the hub stays lit on its detail
      // routes but not on the sibling's subtree. dbg-20260720.
      const excluded = (leaf.excludePaths ?? []).some(
        (ex) => pathname === ex || pathname.startsWith(`${ex}/`),
      );
      if (excluded) return false;
      const paths = [leaf.to, ...(leaf.matchPaths ?? [])];
      for (const p of paths) {
        if (p === '/' || leaf.exact) {
          // Root or hub leaf — exact pathname match only (no prefix), so a
          // hub never co-activates with its deeper sibling routes.
          if (pathname === p) return true;
        } else if (pathname === p || pathname.startsWith(`${p}/`)) {
          return true;
        }
      }
      return false;
    },
    [pathname],
  );

  const groupHasActiveChild = useCallback(
    (group: GroupItem): boolean => group.children.some(isLeafActive),
    [isLeafActive],
  );

  const { toggle, isExpanded } = useExpandedGroups(pathname);

  // Filter visible items per section + drop sections that end up empty.
  const visibleSections = useMemo(() => {
    return SECTIONS.map((section) => {
      const items = section.items
        .map((item) => {
          if (item.kind === 'leaf') {
            return canSee(item.permission) ? item : null;
          }
          // Group: prune children first; section visibility follows.
          const children = item.children.filter((c) => canSee(c.permission));
          if (children.length === 0) return null;
          if (item.permission && !canSee(item.permission)) return null;
          return { ...item, children };
        })
        .filter((x): x is LeafItem | GroupItem => x !== null);
      return { ...section, items };
    }).filter((section) => section.items.length > 0);
  }, [canSee]);

  return (
    <Sidebar header={<PrintwayLogo />}>
      <div className="flex flex-col gap-3">
        {visibleSections.map((section, sectionIdx) => (
          <div key={`section-${sectionIdx}`} className="flex flex-col gap-1">
            {section.headingKey || section.heading ? (
              <div className="mb-1 mt-2 px-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-pw-text-muted">
                  {section.headingKey ? t(section.headingKey) : section.heading}
                </p>
              </div>
            ) : null}
            {section.items.map((item) =>
              item.kind === 'leaf' ? (
                <LeafRow
                  key={item.key}
                  item={item}
                  matchActive={isLeafActive(item)}
                  resolveLabel={resolveLabel as (item: LeafItem) => string}
                />
              ) : (
                <GroupRow
                  key={item.key}
                  item={item}
                  pathname={pathname}
                  isExpanded={isExpanded(item.key)}
                  onToggle={() => toggle(item.key)}
                  childIsActive={groupHasActiveChild(item)}
                  matchLeaf={isLeafActive}
                  resolveLabel={resolveLabel}
                />
              ),
            )}
          </div>
        ))}
      </div>
    </Sidebar>
  );
}

// Re-export helper types so future call sites (Cmd+K palette, etc.) can
// introspect the menu without re-implementing it.
export type { LeafItem, GroupItem, Section };
export { SECTIONS as ADMIN_SIDEBAR_SECTIONS };
